import { useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { Link } from "react-router";
import { useQuery } from "@tanstack/react-query";
import { ArrowRight } from "lucide-react";

import { Button } from "@/components/ui";
import { useAuthStore } from "@/store/authStore";
import { activityService } from "@/services/activityService";
import type { Activity, BookingListFilters } from "@/types";

import {
  BookingsNeedsAttentionSection,
  type AttentionActionFilter,
} from "./BookingsNeedsAttentionSection";
import { ReceiptFlowModals } from "./ReceiptFlowModals";
import { canInteractWithHandshake } from "./sellerBookingRules";
import { useReceiptFlow } from "./useReceiptFlow";

/** Activity owners are scoped to their own activities server-side. */
const OWN_BOOKINGS_SCOPE: BookingListFilters = {};

/**
 * Same "needs attention" section as the Bookings tab, for partner surfaces
 * (e.g. dashboard) that should surface actionable bookings immediately.
 */
export function PartnerNeedsAttentionPanel() {
  const { t } = useTranslation("bookings");
  const { hasPermission } = useAuthStore();

  const [attentionFilter, setAttentionFilter] =
    useState<AttentionActionFilter>("all");
  const receiptFlow = useReceiptFlow({ customerMode: false });

  const { data: activities = [], isLoading: isLoadingActivities } = useQuery<
    Activity[]
  >({
    queryKey: ["activities"],
    queryFn: () => activityService.getAll(),
  });

  const hasActivities = activities.length > 0;

  const activityMap = useMemo(() => {
    const map = new Map<string, Activity>();
    activities.forEach((a) => map.set(a.id, a));
    return map;
  }, [activities]);

  const canInteractHandshake = canInteractWithHandshake({
    customerMode: false,
    adminMode: false,
    hasPermission,
  });

  // Hide only after activities resolve empty. While either query is in flight,
  // keep the section mounted so the loading card is visible (activities and
  // bookings fetch in parallel after the dashboard shell finishes).
  if (!isLoadingActivities && !hasActivities) {
    return null;
  }

  return (
    <>
      <BookingsNeedsAttentionSection
        scope={OWN_BOOKINGS_SCOPE}
        activityMap={activityMap}
        filter={attentionFilter}
        onFilterChange={setAttentionFilter}
        bookingsListPath="/bookings"
        adminMode={false}
        canInteractHandshake={canInteractHandshake}
        processingByBookingId={receiptFlow.processingByBookingId}
        fileInputResetKey={receiptFlow.fileInputResetKey}
        isLoading={isLoadingActivities}
        onReceiptUpload={(booking, file) =>
          void receiptFlow.uploadReceipt(booking, file)
        }
        onManualAmountClick={receiptFlow.openManualAmount}
        footerAction={
          <Link to="/bookings">
            <Button
              type="button"
              variant="ghost"
              size="sm"
              rightIcon={<ArrowRight className="h-4 w-4" />}
            >
              {t("needsAttention.seeAllBookings")}
            </Button>
          </Link>
        }
      />

      <ReceiptFlowModals flow={receiptFlow} />
    </>
  );
}
